import React, { useCallback, useMemo } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Box, Typography } from "@mui/material";
import { CHART_INK } from "../../../../../utils/chartPalette";
import { useSpeciesColors } from "../useSpeciesColors";
import { useChartExport } from "../useChartExport";
import { DIMENSION_LABELS } from "../PivotTable/pivotModel";
import { chartDataRows, chartSeries } from "./chartData";
import { ChartLegend } from "./ChartLegend";

const NUMBER = new Intl.NumberFormat();
const RADIUS = 4;

// A stack segment; the top one of each bar gets the rounded data end.
const segment = (topOf) => (props) => {
  const { x, y, width, height, fill, dataKey, payload } = props;
  if (!height || height <= 0) return null;
  const r = topOf(payload) === dataKey ? Math.min(RADIUS, height, width / 2) : 0;
  const path = r
    ? `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`
    : `M${x},${y}H${x + width}V${y + height}H${x}Z`;
  return <path d={path} fill={fill} stroke="#ffffff" strokeWidth={1} />;
};

// Category labels wrap onto as many lines as they need instead of
// overlapping their neighbors.
const WrappedTick = ({ x, y, payload, width }) => {
  const words = String(payload.value).split(" ");
  const max = Math.max(6, Math.floor((width ?? 80) / 6.5));
  const lines = [];
  words.forEach((w) => {
    const last = lines[lines.length - 1];
    if (last && `${last} ${w}`.length <= max) lines[lines.length - 1] = `${last} ${w}`;
    else lines.push(w);
  });
  return (
    <text x={x} y={y + 12} textAnchor="middle" fill={CHART_INK.secondary} fontSize={12}>
      {lines.slice(0, 3).map((line, i) => (
        <tspan key={i} x={x} dy={i ? 14 : 0}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

// With many bars there is no room to wrap: labels angle down instead,
// shortened past 24 characters (the hover card has the full name).
const AngledTick = ({ x, y, payload }) => {
  const text = String(payload.value);
  const short = text.length > 24 ? `${text.slice(0, 23)}…` : text;
  return (
    <text x={x} y={y + 8} textAnchor="end" fill={CHART_INK.secondary} fontSize={12} transform={`rotate(-35 ${x} ${y + 8})`}>
      <title>{text}</title>
      {short}
    </text>
  );
};

const CROWDED = 14;

/** Round axis ticks from 0 to past `max`, about five of them: 0, 100, 200... */
export const niceTicks = (max, target = 5) => {
  if (!(max > 0)) return [0, 1];
  const raw = max / target;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((m) => m >= raw);
  const whole = Math.max(1, Math.round(step));
  const ticks = [];
  for (let t = 0; t < max + whole; t += whole) ticks.push(t);
  return ticks;
};
const TIME_DIMENSIONS = new Set(["day", "week", "month", "year", "weekOfYear"]);

const HoverCard = ({ active, payload, label, series }) => {
  if (!active || !payload?.length) return null;
  const values = payload[0].payload;
  const shown = [...series].reverse().filter((s) => values[s.key]);
  const total = shown.reduce((sum, s) => sum + values[s.key], 0);
  return (
    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, boxShadow: 2, fontSize: 13 }}>
      <Typography sx={{ fontWeight: 500, fontSize: 13, mb: 0.5 }}>{label}</Typography>
      {shown.map((s) => (
        <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.primary" }}>
          <Box component="span" sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: s.color, flexShrink: 0 }} />
          <span>{s.label}</span>
          <Box component="span" sx={{ ml: "auto", pl: 2, fontVariantNumeric: "tabular-nums" }}>
            {NUMBER.format(values[s.key])}
          </Box>
        </Box>
      ))}
      <Box sx={{ display: "flex", mt: 0.5, pt: 0.5, borderTop: 1, borderColor: "divider", color: "text.secondary" }}>
        <span>Total count</span>
        <Box component="span" sx={{ ml: "auto", pl: 2, fontVariantNumeric: "tabular-nums" }}>
          {NUMBER.format(total)}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * A pivot as a chart: one group on the x-axis per row, one series per column,
 * as stacked bars or as lines. Collection sites stacked by species, species
 * abundance over time. Fills the report area.
 *
 * `controls` sit left of the legend. With `onToggle`, clicking a legend
 * entry hides or shows that series (`hidden` is the set hidden, kept by the
 * caller so it outlives a reload at another grain); the hover card, the axis
 * scale and the downloaded image follow what is shown.
 */
export const SeriesChart = ({ model, title, registerChartExport, mode = "bar", controls, hidden, onToggle }) => {
  const colors = useSpeciesColors();
  const { groups, series: allSeries, omitted } = useMemo(() => chartSeries(model, colors), [model, colors]);
  const series = useMemo(() => allSeries.filter((s) => !hidden?.has(s.key)), [allSeries, hidden]);
  const data = useMemo(() => groups.map((g) => ({ name: g.name, ...g.values })), [groups]);
  const order = useMemo(() => series.map((s) => s.key), [series]);
  const topOf = useCallback((payload) => [...order].reverse().find((k) => payload?.[k] > 0), [order]);
  const Segment = useMemo(() => segment(topOf), [topOf]);

  const axis = DIMENSION_LABELS[model.rowDims[0]] ?? "";
  const getExportSpec = useCallback(
    () => ({
      title,
      legend: series.map((s) => ({ label: s.label, color: s.color })),
      dataRows: chartDataRows(model, axis),
    }),
    [title, series, model, axis]
  );
  useChartExport(registerChartExport, getExportSpec);

  if (!data.length) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography color="text.secondary">No specimens match these filters.</Typography>
      </Box>
    );
  }

  // a time axis keeps its short labels level and thins them out instead
  const timeAxis = TIME_DIMENSIONS.has(model.rowDims[0]);
  const crowded = !timeAxis && data.length > CROWDED;
  const peak = Math.max(
    0,
    ...data.map((d) =>
      mode === "bar"
        ? series.reduce((sum, s) => sum + (d[s.key] ?? 0), 0)
        : Math.max(0, ...series.map((s) => d[s.key] ?? 0))
    )
  );
  const yTicks = niceTicks(peak);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, px: { xs: 2, md: 3 }, py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1, flexWrap: "wrap" }}>
        {controls}
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <ChartLegend items={allSeries} hidden={hidden} onToggle={onToggle} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }} barCategoryGap="24%">
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis
              dataKey="name"
              interval={timeAxis ? "preserveStartEnd" : 0}
              minTickGap={timeAxis ? 28 : 8}
              height={crowded ? 120 : timeAxis ? 32 : 56}
              tickLine={timeAxis ? { stroke: CHART_INK.axis } : false}
              axisLine={{ stroke: CHART_INK.axis }}
              tick={
                timeAxis ? (
                  { fill: CHART_INK.secondary, fontSize: 12 }
                ) : crowded ? (
                  <AngledTick />
                ) : (
                  <WrappedTick width={Math.max(60, 1000 / Math.max(data.length, 1))} />
                )
              }
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: CHART_INK.secondary, fontSize: 12 }}
              tickFormatter={(v) => NUMBER.format(v)}
              width={56}
              ticks={yTicks}
              domain={[0, yTicks[yTicks.length - 1]]}
              allowDecimals={false}
              label={{ value: "Total count", angle: -90, position: "insideLeft", fill: CHART_INK.secondary, fontSize: 12, dy: 40 }}
            />
            <Tooltip
              content={<HoverCard series={series} />}
              cursor={mode === "bar" ? { fill: CHART_INK.grid, fillOpacity: 0.5 } : { stroke: CHART_INK.axis }}
            />
            {series.map((s) =>
              mode === "bar" ? (
                <Bar key={s.key} dataKey={s.key} name={s.label} stackId="stack" fill={s.color} shape={Segment} maxBarSize={72} isAnimationActive={false} />
              ) : (
                <Line
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  type="linear"
                  stroke={s.color}
                  strokeWidth={2}
                  dot={data.length <= 24 ? { r: 4, strokeWidth: 2, fill: "#ffffff" } : false}
                  activeDot={{ r: 5, stroke: "#ffffff", strokeWidth: 2 }}
                  connectNulls
                  isAnimationActive={false}
                />
              )
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 1 }}>
        {axis}
        {omitted.length > 0 &&
          ` · Not shown: ${omitted.map((o) => `${NUMBER.format(o.total)} from ${o.label.toLowerCase()}`).join(", ")}`}
      </Typography>
    </Box>
  );
};
